import React, { useMemo } from 'react';
import { Title, SvgIcon, ConfirmAction, Divider, NewButton, insertSelectItems, FormElement, FORM_ELEMENT_TYPES, validateFlowElement, newTheme, alphabeticalSort } from '../';
import Grid from '@material-ui/core/Grid';
import { $GroupButtons, $GroupContainer, $InviteUser, $AdminEditIcon, $Warning, $WarningContainer, $WarningGrid, $COIComment } from './styles';
import { $FormHelperText } from '../newInput/styles';
const EMPTY_RESOURCES = [];
const ADMIN_EDITABLE_TYPES = [
    FORM_ELEMENT_TYPES.TEXT,
    FORM_ELEMENT_TYPES.TEXT_AREA,
    FORM_ELEMENT_TYPES.EMAIL,
    FORM_ELEMENT_TYPES.DATE,
    FORM_ELEMENT_TYPES.NUMBER,
    FORM_ELEMENT_TYPES.CHECKBOX,
    FORM_ELEMENT_TYPES.TOGGLE,
    FORM_ELEMENT_TYPES.UPLOAD,
    FORM_ELEMENT_TYPES.PHONE_NUMBER,
    FORM_ELEMENT_TYPES.SELECT_PARTITION,
    FORM_ELEMENT_TYPES.PARTITION_RESOURCES,
    FORM_ELEMENT_TYPES.SELECT,
    FORM_ELEMENT_TYPES.MULTISELECT,
    FORM_ELEMENT_TYPES.MULTITEXT,
];
const AdminEditIcon = ({ onClick }) => React.createElement($AdminEditIcon, { onClick: (e) => { e.preventDefault(); onClick(); } },
    React.createElement(SvgIcon, { name: 'altEdit', clickable: true, noPadding: true, size: 16, color: newTheme.colors.text.grey }));
export const ApplicationFlowForm = ({ call, formElements = [], activeCutOff, stepId, profile, assignment, onChangeValue, onChangeFile, openCOI, onAdminEdit, onChangeGroupElementValue, removeInstance, addInstance, options_groups, canEditAssignment, revokedAssignment, showCOI, onDownloadClick, changesRequested, allPartitions, allResources, onInviteUser, documents, documentPreviews, srEmails, loading, }) => {
    const handleOnChange = (elementId, uploadId) => (name, newValue) => handleValueChange(name, newValue, elementId, uploadId);
    const handleValueChange = (name, value, elementId, uploadId, markDirty = true) => {
        if (name === 'file') {
            onChangeFile(elementId, value, uploadId);
        }
        else {
            onChangeValue(value, elementId, true, markDirty);
        }
    };
    const handleOnChangeGroup = (groupInstanceIdx, groupId, childElementId, groupElOrder, elementType, uploadId) => (name, newValue) => handleGroupElementValueChange(name, newValue, groupInstanceIdx, groupId, childElementId, groupElOrder, elementType, uploadId);
    const handleGroupElementValueChange = (name, value, instanceIdx, groupId, childElementId, groupElementOrder, elementType, uploadId, markDirty = true) => {
        if (name === 'file') {
            onChangeFile(childElementId, value, uploadId, groupElementOrder, groupId, instanceIdx);
        }
        else {
            onChangeGroupElementValue(groupId, childElementId, groupElementOrder, instanceIdx, value, elementType, markDirty);
        }
    };
    const handleAutoFill = (id, elementType, autofill, items, parentId, instanceIdx, groupElementIdx) => {
        if (!profile)
            return;
        let autofillVal = profile[autofill];
        if (!autofillVal)
            return;
        if (Array.isArray(autofillVal) && (FORM_ELEMENT_TYPES.MULTISELECT === elementType)) {
            autofillVal = autofillVal.map((val) => {
                const item = (items || [])?.find((item) => item.value == val);
                return item ? val : undefined;
            }).filter((val) => val);
            if (!autofillVal.length)
                return;
        }
        if (elementType === FORM_ELEMENT_TYPES.SELECT) {
            const item = (items || [])?.find((item) => item.value == autofillVal);
            if (!item)
                return;
        }
        if (parentId)
            handleGroupElementValueChange(elementType, autofillVal, instanceIdx || 0, parentId, id, groupElementIdx || 0, elementType, undefined, false);
        else
            handleValueChange(elementType, autofillVal, id, undefined, false);
    };
    const resources = activeCutOff?.resources ? activeCutOff.resources : EMPTY_RESOURCES;
    const enhancedElements = useMemo(() => insertSelectItems(formElements, options_groups), [formElements, options_groups]);
    const allowedResourcePartitions = useMemo(() => canEditAssignment ?
        resources.filter((resource) => !resource.partition.disabled) : allResources, [canEditAssignment, resources, allResources]);
    const partitionOptions = useMemo(() => {
        const partitionIds = [...new Set(allowedResourcePartitions.map((resource) => resource?.partition?.id))];
        return alphabeticalSort(partitionIds.map((id) => {
            const partitionName = allowedResourcePartitions.find((resource) => resource.partition?.id == id)?.partition?.name || '';
            return { value: id, label: partitionName };
        }), 'label');
    }, [allowedResourcePartitions]);
    const enhancedGroupChildrenById = useMemo(() => {
        const map = new Map();
        enhancedElements.forEach((el) => {
            if (el.elementType === FORM_ELEMENT_TYPES.GROUP && el.flow) {
                map.set(el.id, insertSelectItems(el.flow, options_groups));
            }
        });
        return map;
    }, [enhancedElements, options_groups]);
    return React.createElement(React.Fragment, null, enhancedElements.map((element, elementIdx) => {
        const { id, flow, title, config, elementType, type, instances = [], isEditable, noData, previewItems = [] } = element;
        let value = element.value;
        const instanceLength = instances.length;
        const { error, errorMsg, condition, resource, } = validateFlowElement(call, element, stepId, allowedResourcePartitions, instanceLength, undefined, undefined, undefined, options_groups);
        const { visible, required, editable, sum = null } = condition;
        const displayError = element.error && error;
        const elementRequired = required || config?.required;
        const enhancedGroupElements = enhancedGroupChildrenById.get(id) || flow || [];
        const isUpload = elementType === FORM_ELEMENT_TYPES.UPLOAD;
        const upload = documents.find((upload) => upload.id == value);
        const uploadValue = isUpload && value ? upload?.originalFilename : undefined;
        const hasSum = sum !== null;
        const isSelectPartition = elementType === FORM_ELEMENT_TYPES.SELECT_PARTITION;
        const isPartitionResource = elementType === FORM_ELEMENT_TYPES.PARTITION_RESOURCES;
        const elementParams = isPartitionResource && resource ? {
            min: resource.min,
            max: resource.max,
            value: resource.fixed && !value ? resource.fixed : value,
            disabled: !!resource.fixed || !editable || revokedAssignment || !canEditAssignment ||
                (changesRequested && !isEditable),
        } : {};
        const visibleElement = isPartitionResource ? ((!!resource || !isEditable) && visible) : visible;
        const elementEditable = changesRequested ? (canEditAssignment && isEditable) : canEditAssignment;
        if (elementEditable && visible && isPartitionResource && resource?.fixed && (value !== resource.fixed))
            handleValueChange(FORM_ELEMENT_TYPES.PARTITION_RESOURCES, resource.fixed, id, undefined, false);
        if (elementEditable && visible && isPartitionResource && value && !resource)
            handleValueChange(FORM_ELEMENT_TYPES.PARTITION_RESOURCES, null, id, undefined, false);
        if (elementEditable && visible && isSelectPartition && !!assignment?.partitionId && !value && elementRequired &&
            !!partitionOptions.find((p) => p.value == assignment.partitionId))
            handleValueChange(FORM_ELEMENT_TYPES.SELECT_PARTITION, assignment.partitionId, id, undefined, false);
        if (elementEditable && visible && noData && !value && config?.autofill && profile?.id)
            handleAutoFill(id, elementType, config.autofill, element.items);
        if (elementEditable && visible && hasSum && (sum != value))
            handleValueChange(elementType, sum, id, undefined, false);
        if (isSelectPartition && value && !partitionOptions.find((p) => p.value == value)) {
            value = allPartitions?.find((p) => p.id == value)?.name || value;
        }
        const previewValues = previewItems?.map((p) => ({
            ...p,
            datas: p.datas.map((d) => ({
                ...d,
                values: d?.element_type === FORM_ELEMENT_TYPES.SELECT_PARTITION ?
                    ((d.values || []).map((v) => allPartitions?.find((p) => p.id == v)?.name || v)) : d.values,
            })),
        }));
        const isSR = (title || '').toLocaleLowerCase().includes('scientific') && (title || '').toLocaleLowerCase().includes('reviewer');
        const disableUpload = isUpload && loading;
        const showAdminEdit = !!onAdminEdit && !hasSum && ADMIN_EDITABLE_TYPES.includes(elementType);
        if (!visibleElement)
            return null;
        return elementType === FORM_ELEMENT_TYPES.GROUP ?
            instances.map((groupInstance, groupInstanceIdx) => React.createElement($GroupContainer, { container: true, spacing: 2, key: `${groupInstance.id}-${groupInstanceIdx}` },
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Title, { color: displayError ? newTheme.colors.feedback.error : undefined, noMargin: true, bold: false },
                        title,
                        groupInstanceIdx !== 0 ? ` #${groupInstanceIdx + 1}` : '')),
                showCOI && isSR && !canEditAssignment && React.createElement(React.Fragment, null,
                    groupInstance.coi ?
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement($WarningContainer, { item: true },
                                React.createElement($WarningGrid, { container: true, alignItems: 'center' },
                                    React.createElement(SvgIcon, { name: 'warning', color: newTheme.colors.feedback.error, size: 16 }),
                                    React.createElement($Warning, null, "There is a conflict of interest"))))
                        : null,
                    React.createElement(Grid, { item: true, xs: 12 },
                        groupInstance.comment ? React.createElement($COIComment, null,
                            "Comment: ",
                            groupInstance.comment) : null,
                        React.createElement(NewButton, { variant: 'outlined', error: !!groupInstance.coi, onClick: () => openCOI(!!groupInstance.coi, groupInstance.comment || '', groupInstanceIdx, elementIdx, id) }, groupInstance.coi ? 'Edit Conflict' : 'Add Conflict'))),
                enhancedGroupElements.map((groupEl, groupElIdx) => {
                    const childInstance = groupInstance.childInstances
                        .find((child) => child.flowElementId == groupEl.id);
                    let childValue = childInstance?.value;
                    const title = childInstance?.title || groupEl.title;
                    const groupElementIsEditable = childInstance?.isEditable || groupInstance?.isEditable;
                    const { error, errorMsg, condition, resource } = validateFlowElement(call, { ...groupEl, isEditable: groupElementIsEditable }, stepId, allowedResourcePartitions, 0, groupInstance, childValue, changesRequested, options_groups);
                    const { visible, required, editable, sum = null } = condition;
                    const childRequired = required || groupEl.config?.required;
                    const isSelectPartition = groupEl.elementType === FORM_ELEMENT_TYPES.SELECT_PARTITION;
                    const isPartitionResource = groupEl.elementType === FORM_ELEMENT_TYPES.PARTITION_RESOURCES;
                    const elementParams = isPartitionResource && resource ? {
                        min: resource.min,
                        max: resource.max,
                        value: resource.fixed && !childValue ? resource.fixed : childValue,
                        disabled: !!resource.fixed || !editable || revokedAssignment || !canEditAssignment ||
                            (changesRequested && !groupElementIsEditable),
                    } : {};
                    const displayError = childInstance?.error && error;
                    const isUpload = groupEl.elementType === FORM_ELEMENT_TYPES.UPLOAD;
                    const upload = documents.find((upload) => upload.id == childValue);
                    const uploadValue = isUpload && childValue ? upload?.originalFilename : undefined;
                    const visibleElement = isPartitionResource ? ((!!resource || !groupElementIsEditable) && visible) : visible;
                    const elementEditable = changesRequested ? (canEditAssignment && groupElementIsEditable) : canEditAssignment;
                    const hasSum = sum !== null;
                    if (elementEditable && visible && isPartitionResource && resource?.fixed && (childValue !== resource.fixed))
                        handleGroupElementValueChange(FORM_ELEMENT_TYPES.PARTITION_RESOURCES, resource.fixed, groupInstanceIdx, element.id, groupEl.id, groupElIdx, FORM_ELEMENT_TYPES.PARTITION_RESOURCES, undefined, false);
                    if (elementEditable && visible && isPartitionResource && childValue && !resource)
                        handleGroupElementValueChange(FORM_ELEMENT_TYPES.PARTITION_RESOURCES, null, groupInstanceIdx, element.id, groupEl.id, groupElIdx, FORM_ELEMENT_TYPES.PARTITION_RESOURCES, undefined, false);
                    if (elementEditable && visible && childInstance?.noData && !childValue
                        && groupEl.config?.autofill && profile?.id)
                        handleAutoFill(groupEl.id, groupEl.elementType, groupEl.config.autofill, groupEl.items, element.id, groupInstanceIdx, groupElIdx);
                    if (elementEditable && visible && hasSum && (sum != childValue))
                        handleGroupElementValueChange(groupEl.elementType, sum, groupInstanceIdx, element.id, groupEl.id, groupElIdx, groupEl.elementType, undefined, false);
                    const showInviteEmail = !!onInviteUser && isSR &&
                        groupEl.elementType == FORM_ELEMENT_TYPES.EMAIL && !!childValue;
                    const canInvite = !srEmails.includes(String(childValue || '').toLowerCase());
                    const showAdminEdit = !!onAdminEdit && !hasSum &&
                        ADMIN_EDITABLE_TYPES.includes(groupEl.elementType);
                    const disableUpload = isUpload && loading;
                    if (!visibleElement)
                        return null;
                    if (isSelectPartition && childValue && !partitionOptions.find((p) => p.value == childValue)) {
                        childValue = allPartitions?.find((p) => p.id == childValue)?.name || childValue;
                    }
                    const previewValues = groupEl?.previewItems?.map((p) => ({
                        ...p,
                        datas: p.datas.map((d) => ({
                            ...d,
                            values: d?.element_type === FORM_ELEMENT_TYPES.SELECT_PARTITION ?
                                ((d.values || []).map((v) => allPartitions?.find((p) => p.id == v)?.name || v)) : d.values,
                        })),
                    }));
                    return React.createElement(React.Fragment, null,
                        React.createElement(Grid, { item: true, xs: showInviteEmail ? 11 : 12, key: `${groupInstance.id}-${groupEl.id}`, id: `${groupEl.id}-${groupInstanceIdx}` },
                            React.createElement(FormElement, { element: {
                                    ...groupEl,
                                    allowFormat: !hasSum,
                                    id: `${groupInstance.id}-${groupEl.id}`,
                                    items: isSelectPartition ? partitionOptions : groupEl.items,
                                    debounce: true,
                                    value: isUpload ? uploadValue : hasSum ? sum : childValue,
                                    type: groupEl.type,
                                    elementType: groupEl.elementType,
                                    disabled: !editable || disableUpload || revokedAssignment || !canEditAssignment || hasSum ||
                                        (changesRequested && !groupElementIsEditable),
                                    required: childRequired,
                                    error: displayError,
                                    errorMsg: displayError ? errorMsg : '',
                                    hideItalic: true,
                                    name: title,
                                    title,
                                    onDownloadClick: () => onDownloadClick(upload?.id || 0, groupEl.id),
                                    onUploadClick: (value) => onDownloadClick(Number(value) || 0, groupEl.id),
                                    onChange: handleOnChangeGroup(groupInstanceIdx, element.id, groupEl.id, groupElIdx, groupEl.elementType, upload?.id),
                                    ...elementParams,
                                    previewItems: previewValues,
                                    actionIcon: showAdminEdit ?
                                        React.createElement(AdminEditIcon, { onClick: () => onAdminEdit(groupEl, groupInstance.id || undefined) }) : undefined,
                                } })),
                        showInviteEmail &&
                            React.createElement($InviteUser, { item: true, xs: 1 },
                                React.createElement(SvgIcon, { name: 'person-add', color: canInvite ? 'primary' : newTheme.colors.grey, clickable: true, size: 24, onClick: () => { if (onInviteUser && canInvite)
                                        onInviteUser(groupInstance); } })));
                }),
                canEditAssignment ?
                    React.createElement(React.Fragment, null,
                        React.createElement($GroupButtons, { container: true, xs: 12 },
                            (instanceLength > 1 || (groupInstanceIdx > 0)) &&
                                (changesRequested ? groupInstance?.isEditable : true) &&
                                React.createElement(ConfirmAction, { disabled: loading, deletion: true, onConfirm: () => removeInstance(element.id, groupInstanceIdx), title: `Remove "${title} #${groupInstanceIdx + 1}"`, description: `All data inside this "${title}" will be deleted.` },
                                    React.createElement(NewButton, { disabled: loading, variant: 'outlined', error: true }, "Remove")),
                            (groupInstanceIdx >= (instanceLength - 1)) && (changesRequested ? groupInstance?.isEditable : true) &&
                                React.createElement(NewButton, { disabled: !!(config?.max && (instanceLength >= config.max)) || loading, creator: true, variant: 'outlined', onClick: () => addInstance(element, instanceLength) }, title)),
                        displayError && React.createElement($FormHelperText, { error: true }, errorMsg),
                        (groupInstanceIdx < (instanceLength - 1)) && React.createElement(Divider, { noMarginBottom: false }))
                    : null))
            :
                React.createElement(Grid, { item: true, xs: 12, key: id, id: String(id) },
                    React.createElement(FormElement, { element: {
                            ...element,
                            items: isSelectPartition ? partitionOptions : element.items,
                            debounce: true,
                            allowFormat: !hasSum,
                            value: isUpload ? uploadValue : hasSum ? sum : value,
                            type: type,
                            elementType: elementType,
                            disabled: !editable || disableUpload || revokedAssignment || !canEditAssignment || hasSum ||
                                (changesRequested && !isEditable),
                            required: elementRequired,
                            error: displayError,
                            name: title,
                            title,
                            errorMsg: displayError ? errorMsg : '',
                            onDownloadClick: () => onDownloadClick(upload?.id || 0, id),
                            onUploadClick: (value) => onDownloadClick(Number(value) || 0, id),
                            hideItalic: true,
                            onChange: handleOnChange(id, upload?.id),
                            ...elementParams,
                            previewItems: previewValues,
                            documents: documentPreviews?.[id],
                            actionIcon: showAdminEdit ? React.createElement(AdminEditIcon, { onClick: () => onAdminEdit(element) }) : undefined,
                        } }));
    }));
};
